import { containsAnyPattern, getUserAgent } from "@/utils/sentryHelpers";
import * as Sentry from "@sentry/vue";
import { type AxiosError, isAxiosError, isCancel } from "axios";
import type { App } from "vue";
import type { Router } from "vue-router";

interface ExtendedAxiosError extends AxiosError {
  code?: string;
  message: string;
}

// has user cancelled request (navigated away, etc.)
// ideas: https://github.com/axios/axios/issues/6209
const isNavCancel = (e: AxiosError): boolean =>
  isCancel(e) ||
  (e as ExtendedAxiosError).code === "ERR_CANCELED" ||
  ((e as ExtendedAxiosError).code === "ECONNABORTED" &&
    /aborted|canceled/i.test((e as ExtendedAxiosError).message));

// only report unexpected axios errors
const shouldReportAxios = (e: AxiosError): boolean => {
  if (isNavCancel(e)) return false;
  if (!e.response) return false;
  const s = e.response.status;

  // only report errors with status >= 500
  return s >= 500;
};

// drops stale-chunk errors
const isChunkLoadError = (err: unknown, event?: Sentry.Event): boolean => {
  const patterns = [
    "couldn't resolve component",
    "failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "importing a module script failed",
    "unable to preload css for",
    "vite-chunk-reload",
  ];

  return containsAnyPattern(err, event, patterns);
};

// drops abort errors
const isAbortDomException = (err: unknown): boolean =>
  err instanceof DOMException && err.name === "AbortError";

// drops Windows-specific "Object Not Found Matching Id" errors
// See ticket => https://github.com/101skills-gmbh/fobizz-rails/issues/4146
const isWindowsObjectNotFoundError = (
  err: unknown,
  event?: Sentry.Event
): boolean => {
  const userAgent = getUserAgent(event);
  const isWindows = userAgent.toLowerCase().includes("windows");

  return (
    isWindows &&
    containsAnyPattern(err, event, ["object not found matching id"])
  );
};

// filter out browser extensions
const denyUrls = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^ms-browser-extension:/i,
];

// https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
export function initSentry(app: App, router: Router): void {
  Sentry.init({
    app,
    dsn: "https://d4c7a50c02ac7526e58ae33453e7d5a3@relay.fobizz.com/4507055244050512",
    // Git SHA baked in at Vite build time (see Dockerfile VITE_RELEASE).
    // Undefined in local dev, which leaves the release untagged there.
    release: import.meta.env.VITE_RELEASE ?? undefined,
    denyUrls,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.extraErrorDataIntegration({ depth: 4 }),
    ],
    tracesSampleRate: 0.01,
    tracePropagationTargets: ["localhost", /^https:\/\/app\.fobizz\.com\/api/],
    beforeSend(event, hint) {
      const err = hint?.originalException;

      if (
        isChunkLoadError(err, event) ||
        isAbortDomException(err) ||
        isWindowsObjectNotFoundError(err, event)
      )
        return null;

      if (isAxiosError(err)) {
        if (!shouldReportAxios(err)) return null;
      }

      return event;
    },
  });
}

/**
 * Set Sentry user context with pseudonymous ID (GDPR-compliant)
 * Uses toolsUserId which is available on all user types
 */
export function setSentryUser(toolsUserId: string | undefined): void {
  if (toolsUserId) {
    Sentry.setUser({ id: toolsUserId });
  }
}

/**
 * Clear Sentry user context (call on logout)
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}
